import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/cn";
import type { OptionItem } from "../types";
import { ScreenShell } from "./ScreenShell";

interface OptionScreenProps {
  readonly title: string;
  readonly subtext?: string;
  readonly options: readonly OptionItem[];
  readonly value?: string;
  /** Option rendered pre-highlighted when nothing is selected yet (e.g. "Lose weight"). */
  readonly primaryValue?: string;
  readonly onSelect: (value: string) => void;
  readonly onAdvance: () => void;
}

const ADVANCE_DELAY_MS = 250;

/** Single-select radio card list with auto-advance after a short selection beat. */
export function OptionScreen({
  title,
  subtext,
  options,
  value,
  primaryValue,
  onSelect,
  onAdvance,
}: OptionScreenProps) {
  const [selected, setSelected] = useState<string | undefined>(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, []);

  const handleSelect = (next: string) => {
    if (timerRef.current !== null) return; // ignore double taps during the feedback beat
    setSelected(next);
    onSelect(next);
    timerRef.current = setTimeout(() => {
      onAdvance();
    }, ADVANCE_DELAY_MS);
  };

  return (
    <ScreenShell title={title} subtext={subtext}>
      <div role="radiogroup" aria-label={title} className="flex flex-col gap-3">
        {options.map((option) => {
          const isSelected = selected === option.value;
          const isPrimaryPreview = selected === undefined && primaryValue === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => handleSelect(option.value)}
              className={cn(
                "flex min-h-[56px] w-full items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left",
                "select-none transition-all duration-200 ease-out active:scale-[0.98]",
                isSelected || isPrimaryPreview
                  ? "border-accent bg-accent-light"
                  : "border-border bg-surface",
              )}
            >
              <span className="flex flex-1 flex-col gap-0.5">
                <span className="flex items-center gap-2">
                  <span className="text-base font-semibold text-text">{option.title}</span>
                  {option.badge && (
                    <span className="rounded-full bg-accent px-2 py-0.5 text-[11px] font-semibold text-white">
                      {option.badge}
                    </span>
                  )}
                </span>
                {option.sublabel && (
                  <span className="text-sm text-text-muted">{option.sublabel}</span>
                )}
              </span>
              {isSelected && (
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent text-white">
                  <Check size={16} strokeWidth={3} aria-hidden="true" />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </ScreenShell>
  );
}
